import { useEffect, useMemo } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router';
import { observer } from 'mobx-react';
import { Page } from '@roc/feature-app-core';
import { loadPlatformWebComponents } from '@roc/feature-utils';
import { useStore } from '../../hooks';

const BorrowerLoanSubmissionEvent = {
  SUBMITTED: 'platform-ui:borrower-loan-submission:submitted',
  VALIDATION_ERROR: 'platform-ui:borrower-loan-submission:validation-error',
  PRODUCT_SELECTED: 'platform-ui:borrower-loan-submission:product-selected',
  CREATE_ACCOUNT: 'platform-ui:borrower-loan-submission:create-account',
  RETURN_HOME: 'platform-ui:borrower-loan-submission:return-home',
} as const;

interface BorrowerLoanSubmissionEventDetail {
  loanId?: string | null;
  message?: string;
  product?: string | null;
  borrowerId?: string | null;
}

interface PublicLoanSubmissionPlatformUiProps {
  currentRoutes: any;
}

export const PublicLoanSubmissionPlatformUi = observer(
  ({ currentRoutes }: PublicLoanSubmissionPlatformUiProps) => {
    const { push } = useHistory();
    const { globalStore } = useStore();
    const { assignee } = useParams<{ assignee?: string }>();
    const match = useRouteMatch();

    const products = useMemo(
      () => [
        { slug: 'fix-flip', route: currentRoutes.children.fixFlip },
        { slug: 'ground-up', route: currentRoutes.children.groundUp },
        {
          slug: 'single-property',
          route: currentRoutes.children.singleProperty,
        },
      ],
      [currentRoutes]
    );

    const matched = products.find(({ route }) => route.path === match.path);
    const routeDefinition = matched?.route ?? currentRoutes;

    useEffect(() => {
      loadPlatformWebComponents();
    }, []);

    useEffect(() => {
      const withAssignee = (url: string) =>
        assignee ? `${url}/${assignee}` : url;

      const handleProductSelected = (e: Event) => {
        const { detail } = e as CustomEvent<BorrowerLoanSubmissionEventDetail>;
        const url = detail?.product
          ? products.find(({ slug }) => slug === detail.product)?.route.url
          : currentRoutes.url;
        if (url) {
          push(withAssignee(url));
        }
      };

      const handleValidationError = (e: Event) => {
        const { detail } = e as CustomEvent<BorrowerLoanSubmissionEventDetail>;
        globalStore.notificationStore.showErrorNotification({
          message: detail?.message || 'Please fix the validation errors.',
        });
      };

      const handleCreateAccount = (e: Event) => {
        const { detail } = e as CustomEvent<BorrowerLoanSubmissionEventDetail>;
        const borrowerId = detail?.borrowerId;
        if (!borrowerId) return;
        push(
          `/createBorrowerAccount/${window.btoa(
            encodeURIComponent(borrowerId)
          )}`
        );
      };

      // There is no dashboard to return to without a session.
      const handleReturnHome = () => {
        push(withAssignee(currentRoutes.url));
      };

      window.addEventListener(
        BorrowerLoanSubmissionEvent.PRODUCT_SELECTED,
        handleProductSelected
      );
      window.addEventListener(
        BorrowerLoanSubmissionEvent.VALIDATION_ERROR,
        handleValidationError
      );
      window.addEventListener(
        BorrowerLoanSubmissionEvent.CREATE_ACCOUNT,
        handleCreateAccount
      );
      window.addEventListener(
        BorrowerLoanSubmissionEvent.RETURN_HOME,
        handleReturnHome
      );
      return () => {
        window.removeEventListener(
          BorrowerLoanSubmissionEvent.PRODUCT_SELECTED,
          handleProductSelected
        );
        window.removeEventListener(
          BorrowerLoanSubmissionEvent.VALIDATION_ERROR,
          handleValidationError
        );
        window.removeEventListener(
          BorrowerLoanSubmissionEvent.CREATE_ACCOUNT,
          handleCreateAccount
        );
        window.removeEventListener(
          BorrowerLoanSubmissionEvent.RETURN_HOME,
          handleReturnHome
        );
      };
    }, [push, globalStore, currentRoutes, products, assignee]);

    return (
      <Page routeDefinition={routeDefinition}>
        <platform-ui
          id="platform-ui-public-loan-submission"
          component="borrower_loan_submission_public"
          data-public-mode="true"
          data-product={matched?.slug}
          data-host={globalStore?.portalConfiguration?.host}
          data-portal-id={globalStore?.portalConfiguration?.id}
          data-assignee={assignee}
        />
      </Page>
    );
  }
);
